import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

// UK-style yellow reg-plate lookup → tries DVLA → routes to /booking
export default function RegLookup() {
  const navigate = useNavigate();
  const [reg, setReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [notice, setNotice] = useState("");

  const handleChange = (e) => {
    const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, "").slice(0, 8);
    setReg(cleaned);
    setVehicle(null);
    setNotice("");
  };

  const buildVehicleString = (v) => {
    const parts = [v.make, v.year, v.colour].filter(Boolean);
    return parts.join(" · ");
  };

  const goToBooking = (vehicleStr, vehicleMake, vehicleYear) => {
    const compact = reg.trim().replace(/\s+/g, "");
    const params = new URLSearchParams({ reg: compact });
    if (vehicleStr) params.set("vehicle", vehicleStr);
    if (vehicleMake) params.set("make", vehicleMake);
    if (vehicleYear) params.set("year", String(vehicleYear));
    navigate(`/booking?${params.toString()}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reg.trim()) return;
    setLoading(true);
    setNotice("");
    try {
      const compact = reg.trim().replace(/\s+/g, "");
      const res = await api.post("/vehicle/lookup", { reg: compact });
      setVehicle(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 503 || detail === "lookup_not_configured") {
        // Lookup not enabled — silently continue
        goToBooking(null);
        return;
      }
      if (status === 404 || detail === "vehicle_not_found") {
        setNotice("Couldn't find that reg on DVLA. Continue with the booking anyway?");
      } else {
        setNotice("Lookup unavailable. Continue with the booking.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 relative" data-testid="reg-lookup-section">
      <div className="max-w-5xl mx-auto px-6 relative">
        <div className="absolute inset-0 -z-10 starfield opacity-30" />

        <div className="relative rounded-sm border border-white/10 bg-gradient-to-br from-[#0c0c0c] via-[#0a0a0a] to-[#0c0c0c] p-8 sm:p-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#D946EF] text-white text-[10px] font-bold tracking-[0.2em]">
                  START HERE
                </span>
                <span className="text-xs tracking-[0.35em] text-[#D946EF]">/ INSTANT QUOTE</span>
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                Drop your reg.<br />
                See <span className="neon-cyan">your car's packages.</span>
              </h2>
              <p className="mt-4 text-zinc-400 font-body max-w-md">
                Type your number plate and we'll auto-match your make/model, surface the
                <b className="text-white"> exact packages your car supports</b> (Signature Premium
                for Mercedes, Screen Code for compatible BMWs, Audis & VWs), and prep the right
                kit before you arrive.
              </p>
            </div>

            <form
              onSubmit={submit}
              className="w-full lg:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
              data-testid="reg-lookup-form"
            >
              <div className="flex items-stretch rounded-md overflow-hidden shadow-[0_0_30px_rgba(255,213,0,0.3)] border-2 border-black/40">
                <div className="bg-[#003399] text-white px-3 flex flex-col items-center justify-center min-w-[34px]">
                  <div className="text-yellow-400 text-[10px] font-bold leading-none">★★★</div>
                  <div className="text-yellow-400 text-[10px] font-bold leading-none mt-0.5">GB</div>
                </div>
                <input
                  data-testid="reg-input"
                  value={reg}
                  onChange={handleChange}
                  placeholder="YOUR REG"
                  aria-label="Vehicle registration"
                  spellCheck="false"
                  autoComplete="off"
                  className="bg-[#FFD500] text-black font-display font-black text-2xl sm:text-3xl tracking-[0.18em] uppercase placeholder:text-black/40 px-5 py-4 w-full sm:w-[260px] outline-none border-0"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                />
              </div>
              <button
                type="submit"
                disabled={!reg.trim() || loading}
                data-testid="reg-submit-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#D946EF] text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#F0ABFC] transition whitespace-nowrap"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {loading ? "Checking…" : "Continue booking"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </div>

          {/* Vehicle preview after successful lookup */}
          {vehicle && (
            <div data-testid="vehicle-preview" className="mt-8 p-6 rounded-sm border border-[#D946EF]/30 bg-[#D946EF]/5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#D946EF]" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-[#D946EF] mb-0.5">DVLA MATCH</div>
                  <div className="font-display font-bold text-xl">
                    {vehicle.make || "Vehicle"} {vehicle.year ? `· ${vehicle.year}` : ""}
                    {vehicle.colour ? ` · ${vehicle.colour}` : ""}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {[vehicle.fuel, vehicle.engine_cc && `${vehicle.engine_cc}cc`, vehicle.mot_status && `MOT ${vehicle.mot_status}`].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
              <button
                onClick={() => goToBooking(buildVehicleString(vehicle), vehicle.make, vehicle.year)}
                data-testid="vehicle-preview-continue"
                className="sm:ml-auto inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D946EF] text-white font-semibold hover:bg-[#F0ABFC] transition"
              >
                Continue with this vehicle <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Soft notice when lookup couldn't find / failed */}
          {notice && !vehicle && (
            <div className="mt-6 p-4 rounded-sm border border-yellow-500/30 bg-yellow-500/5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="text-sm text-zinc-300">{notice}</div>
              <button
                onClick={() => goToBooking(null, null)}
                data-testid="lookup-fallback-continue"
                className="sm:ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition"
              >
                Continue anyway <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          <p className="mt-6 text-xs text-zinc-600 font-body">
            We use your reg only to confirm fitment for your booking. We don't store or share it.
          </p>
        </div>
      </div>
    </section>
  );
}
